import { useEffect } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import {
  Table,
  Stack,
  Dialog,
  Button,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  Typography,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { enqueueSnackbar } from 'notistack';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import useProductOptions from 'src/hooks/use-product-options';
import FormProvider, { RHFTextField } from 'src/components/hook-form';
import { ProductQuotaType } from 'src/type/product';
import axios, { endpoints } from 'src/utils/axios';
import { toastError } from 'src/utils/format-error';
import { UserType } from 'src/type/user';

type FormValues = {
  quotas: {
    productId: string;
    type: ProductQuotaType;
    amount: number;
  }[];
};

type Props = {
  open: boolean;
  onClose: VoidFunction;
  user: UserType;
  handleReload: VoidFunction;
};

export default function QuotasCreateDialog({ open, onClose, user, handleReload }: Props) {
  const { productOptions } = useProductOptions(true, user.uid);

  const defaultValues: FormValues = {
    quotas: [],
  };

  const Schema = Yup.object().shape({
    quotas: Yup.array().of(
      Yup.object().shape({
        productId: Yup.string().required(),
        type: Yup.string().required(),
        amount: Yup.number()
          .typeError('จำนวนต้องเป็นตัวเลข')
          .required('จำเป็นต้องระบุจำนวน')
          .moreThan(-1, 'จำนวนขั้นต่ำ 0'),
      })
    ),
  });

  const methods = useForm<FormValues>({
    mode: 'onChange',
    resolver: yupResolver(Schema) as any,
    defaultValues,
  });

  const {
    handleSubmit,
    reset,
    control,
    formState: { isSubmitting },
  } = methods;
  const { fields, replace } = useFieldArray({
    control,
    name: 'quotas',
  });

  // Auto fill data once modal opens
  useEffect(() => {
    if (open && productOptions.length > 0) {
      const data: FormValues['quotas'] = [];

      productOptions.forEach((product) => {
        [ProductQuotaType.BUY, ProductQuotaType.SELL].forEach((type) => {
          const found = user?.userProductPositionQuotas?.find(
            (q) => q.productId === product.value && q.type === type
          );
          data.push({
            productId: product.value,
            type,
            amount: found?.amount && found.amount > 0 ? found.amount : 0,
          });
        });
      });

      replace(data);
    }
  }, [open, productOptions]);

  const onSubmit = handleSubmit(async (formValues) => {
    try {
      const body = formValues.quotas.map((item) => ({
        userId: user.uid,
        productId: item.productId,
        amount: Number(item.amount),
        type: item.type,
      }));

      // Use Promise.all to wait for all POST requests to complete
      await Promise.all(body.map((item) => axios.post(endpoints.productPositionQuotas.post, item)));

      enqueueSnackbar('ทำรายการได้สำเร็จ');
      handleReload();
      beforeClose();
    } catch (error) {
      toastError(error);
    }
  });

  const beforeClose = () => {
    reset();
    onClose();
  };

  // const getProductLabel = (productId: string) => {
  //   return productOptions.find((p) => p.value === productId)?.label || '-';
  // };

  return (
    <Dialog open={open} onClose={beforeClose} maxWidth="md" fullWidth>
      <FormProvider methods={methods} onSubmit={onSubmit}>
        <DialogTitle>แก้ไขปริมาณที่ซื้อขายได้</DialogTitle>

        <DialogContent>
          <Stack spacing={2}>
            {/* Responsive Table Wrapper */}
            <div style={{ overflowX: 'auto' }}>
              <Table sx={{ minWidth: 600 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>สินค้า</TableCell>
                    <TableCell align="right">ซื้อ (กรัม)</TableCell>
                    <TableCell align="right">ขาย (กรัม)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {productOptions.map((product) => {
                    const buyField = fields.find(
                      (f) => f.productId === product.value && f.type === ProductQuotaType.BUY
                    );
                    const sellField = fields.find(
                      (f) => f.productId === product.value && f.type === ProductQuotaType.SELL
                    );
                    return (
                      <TableRow key={product.value}>
                        <TableCell>
                          <Typography>{product.label}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          {buyField && (
                            <RHFTextField
                              name={`quotas.${fields.indexOf(buyField)}.amount`}
                              type="number"
                              size="small"
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <Typography fontWeight="bold" color="success.main">
                                      กรัม
                                    </Typography>
                                  </InputAdornment>
                                ),
                              }}
                            />
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {sellField && (
                            <RHFTextField
                              name={`quotas.${fields.indexOf(sellField)}.amount`}
                              type="number"
                              size="small"
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <Typography fontWeight="bold" color="success.main">
                                      กรัม
                                    </Typography>
                                  </InputAdornment>
                                ),
                              }}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" onClick={beforeClose} disabled={isSubmitting}>
            ยกเลิก
          </Button>
          <LoadingButton type="submit" variant="contained" loading={isSubmitting}>
            บันทึก
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}
